"use client";

import { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Edit,
  Trash2,
  Loader2,
  Plus,
  Eye,
  ClipboardList
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableCaption,
} from "@/components/ui/table";

import { toast } from "sonner";

import {
  useGetMockTestsQuery,
  useDeleteMockTestMutation,
} from "@/features/api/mockTestApi";

export default function MockTestsList() {

  const navigate = useNavigate();

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetMockTestsQuery();

  const [deleteMockTest, { isLoading: isDeleting }] =
    useDeleteMockTestMutation();

  const mockTests = useMemo(
    () => (Array.isArray(data?.mockTests) ? data.mockTests : []),
    [data]
  );

  const handleDelete = async (e, id) => {
    e.stopPropagation();

    if (!id) return;

    if (window.confirm("Delete this mock test?")) {
      try {
        await deleteMockTest(id).unwrap();
        toast.success("Mock test deleted");
      } catch (err) {
        toast.error(err?.data?.message || "Delete failed");
      }
    }
  };

  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message || "Failed to load mock tests");
    }
  }, [isError, error]);

  return (
    <div className="flex-1 p-6 bg-background min-h-screen">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">

        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <ClipboardList size={26} />
          All Mock Tests
        </h2>

        <div className="flex items-center gap-2">

          <Button
            variant="outline"
            onClick={() => window.open("/online-payment", "_blank")}
            className="flex items-center gap-2"
          >
            <Eye size={18} />
            View Page
          </Button>

          <Button
            onClick={() => navigate("/admin/mock-tests/create")}
            className="flex items-center gap-2"
          >
            <Plus size={18} />
            Create Mock Test
          </Button>

        </div>

      </div>

      {isLoading || isFetching ? (
        <div className="flex justify-center items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading mock tests...
        </div>
      ) : mockTests.length === 0 ? (
        <div className="text-center text-gray-500">
          📭 No mock tests found
        </div>
      ) : (

        <Table>

          <TableCaption>
            List of all mock tests
          </TableCaption>

          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>

            {mockTests.map((test) => (

              <TableRow
                key={test._id}
                onClick={() =>
                  navigate(`/admin/mock-tests/${test._id}/edit`)
                }
                className="hover:bg-accent/30 cursor-pointer"
              >

                <TableCell className="font-medium">
                  {test.name}
                </TableCell>

                <TableCell>
                  ₹{test.price}
                </TableCell>

                <TableCell>

                  {test.status === "published" ? (
                    <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                      Published
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs rounded bg-gray-200 text-gray-700">
                      Draft
                    </span>
                  )}

                </TableCell>

                <TableCell
                  className="flex gap-2 justify-end"
                  onClick={(e) => e.stopPropagation()}
                >

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      navigate(`/admin/mock-tests/${test._id}/edit`)
                    }
                  >
                    <Edit size={18} />
                  </Button>

                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={(e) =>
                      handleDelete(e, test._id)
                    }
                    disabled={isDeleting}
                  >
                    <Trash2 size={18} />
                  </Button>

                </TableCell>

              </TableRow>
            ))}

          </TableBody>

        </Table>
      )}
    </div>
  );
}
